import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Trash2, Database, Clock, CheckCircle, Mail, AlertTriangle } from 'lucide-react';

export default function DataDeletion() {
  const lastUpdated = "April 4, 2026";

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0A0A1B 0%, #0D0D1F 100%)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'rgba(10, 10, 27, 0.8)', borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold">Back to Privluxe</span>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
            <span className="text-sm" style={{ color: 'rgba(229,231,235,0.6)' }}>Legal</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.2) 0%, rgba(124,58,237,0.2) 100%)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <Trash2 className="h-8 w-8" style={{ color: '#EF4444' }} />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Data Deletion Policy</h1>
          <p style={{ color: 'rgba(229,231,235,0.6)' }}>Last updated: {lastUpdated}</p>
        </div>

        {/* Introduction */}
        <div className="p-6 rounded-2xl mb-12" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
          <p className="text-lg text-center" style={{ color: '#E5E7EB' }}>
            <span style={{ color: '#22C55E' }}>Your data, your control.</span><br />
            Learn how to delete your data and what happens when you do.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-10">
          {/* In-App Deletion Options */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <Trash2 className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">In-App Deletion Options</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>You can delete your data directly within Privluxe:</p>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" style={{ color: '#22C55E' }} />
                    Delete Individual Messages
                  </h4>
                  <p className="text-sm">Long-press any message → Select "Delete" → Choose "Delete for Everyone" or "Delete for Me"</p>
                </div>
                
                <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" style={{ color: '#22C55E' }} />
                    Clear Entire Conversation
                  </h4>
                  <p className="text-sm">Open conversation → Tap menu (⋮) → Select "Clear Chat" → Confirm deletion</p>
                </div>
                
                <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" style={{ color: '#22C55E' }} />
                    Delete Conversation
                  </h4>
                  <p className="text-sm">Swipe left on conversation in chat list → Tap "Delete" → Confirm</p>
                </div>
                
                <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" style={{ color: '#22C55E' }} />
                    Use Disappearing Messages
                  </h4>
                  <p className="text-sm">Enable in Settings → Messages are automatically deleted after the set time period</p>
                </div>
                
                <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" style={{ color: '#22C55E' }} />
                    View-Once Media
                  </h4>
                  <p className="text-sm">Send photos/videos as view-once → Automatically deleted after recipient views</p>
                </div>
              </div>
            </div>
          </section>

          {/* Account Deletion */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <Database className="h-5 w-5" style={{ color: '#EF4444' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Delete Your Account</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>To completely delete your account and all associated data:</p>
              
              <div className="p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <h4 className="font-semibold text-white mb-3">Option 1: In-App Deletion</h4>
                <ol className="list-decimal pl-5 space-y-2 text-sm">
                  <li>Go to Settings → Account → Delete Account</li>
                  <li>Enter your password to confirm identity</li>
                  <li>Review what will be deleted</li>
                  <li>Tap "Permanently Delete My Account"</li>
                </ol>
              </div>
              
              <div className="p-4 rounded-xl" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
                <h4 className="font-semibold text-white mb-3">Option 2: Email Request</h4>
                <p className="text-sm mb-2">Send a deletion request to: <strong>privacy@privluxe.com</strong></p>
                <p className="text-sm">Include your registered phone number or email for verification.</p>
              </div>
            </div>
          </section>

          {/* What Gets Deleted */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.2)' }}>
                <CheckCircle className="h-5 w-5" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">What Gets Deleted</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>When you delete your account, the following data is permanently removed:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your profile information (username, phone, email, photo)</li>
                <li>All your messages in conversations</li>
                <li>All media you've sent (photos, videos, voice notes)</li>
                <li>Your contact list and friend connections</li>
                <li>Call history and logs</li>
                <li>Notification preferences and settings</li>
                <li>Any pending invites you've sent</li>
              </ul>
            </div>
          </section>

          {/* What May Be Retained */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.2)' }}>
                <AlertTriangle className="h-5 w-5" style={{ color: '#F59E0B' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Data That May Be Retained</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>Certain data may be retained for limited periods as required by law or for legitimate business purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Security logs:</strong> Retained for up to 90 days for fraud prevention</li>
                <li><strong className="text-white">Backup systems:</strong> May contain data for up to 30 days before automatic purge</li>
                <li><strong className="text-white">Legal holds:</strong> Data subject to legal proceedings may be preserved</li>
                <li><strong className="text-white">Abuse reports:</strong> Evidence of policy violations may be retained</li>
                <li><strong className="text-white">Anonymized analytics:</strong> Aggregated, non-identifiable usage data</li>
              </ul>
            </div>
          </section>

          {/* Timeline */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,211,238,0.2)' }}>
                <Clock className="h-5 w-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Deletion Timeline</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="w-24 shrink-0 text-right font-semibold" style={{ color: '#22D3EE' }}>Immediate</div>
                  <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
                  <div className="flex-1">Account access disabled, profile hidden</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24 shrink-0 text-right font-semibold" style={{ color: '#22D3EE' }}>24-48 hours</div>
                  <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
                  <div className="flex-1">Messages and media removed from active systems</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24 shrink-0 text-right font-semibold" style={{ color: '#22D3EE' }}>30 days</div>
                  <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
                  <div className="flex-1">Data purged from backup systems</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24 shrink-0 text-right font-semibold" style={{ color: '#22D3EE' }}>90 days</div>
                  <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
                  <div className="flex-1">Security logs purged (unless legal hold)</div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(124,58,237,0.2)' }}>
                <Mail className="h-5 w-5" style={{ color: '#A78BFA' }} />
              </div>
              <h2 className="text-2xl font-bold text-white">Contact Us</h2>
            </div>
            <div className="space-y-4" style={{ color: 'rgba(229,231,235,0.8)' }}>
              <p>For data deletion requests or questions about your data:</p>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <p><strong className="text-white">Email:</strong> privacy@privluxe.com</p>
                <p><strong className="text-white">Subject Line:</strong> Data Deletion Request - [Your Username]</p>
                <p className="mt-2 text-sm">We will respond within 30 days and complete deletion within the timelines above.</p>
              </div>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-16 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h3 className="text-lg font-semibold text-white mb-4">Related Documents</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/privacy" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#A78BFA' }} />
              <span className="text-sm text-white">Privacy Policy</span>
            </Link>
            <Link to="/terms" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)' }}>
              <Shield className="h-6 w-6 mx-auto mb-2" style={{ color: '#22D3EE' }} />
              <span className="text-sm text-white">Terms & Conditions</span>
            </Link>
            <Link to="/cookies" className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <Database className="h-6 w-6 mx-auto mb-2" style={{ color: '#22C55E' }} />
              <span className="text-sm text-white">Cookie Policy</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-16" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <p style={{ color: 'rgba(229,231,235,0.4)' }}>© 2026 Privluxe. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
